<template>
	<div class="notification-post-container">
		<LayoutBox class="notification-post">
			<header class="notification-post__header">
				<div class="controls">
					<AppDropdown
						class="controls__dropdown"
						:options="['Latest', 'Oldest']"
						placeholder="Sort by"
						v-model="filters.sort"
						@input="getList"
					>
						Sort By
					</AppDropdown>
					<AppDropdown
						class="controls__dropdown"
						:options="['All', 'Posted', 'In Queue']"
						placeholder="Filter by"
						v-model="filters.status"
						@input="getList"
					>
						Filter By
					</AppDropdown>
					<AppButton
						class="controls__button"
						modifier="small-danger"
						v-if="selected.length > 1"
                        @click="deleteItem(true)"
					>
						Delete
					</AppButton>
				</div>
				<div class="controls">
					<AppButton
						class="controls__button"
						modifier="small-primary"
						@click="$router.push({ name: 'create-notification' })"
					>
						Create
					</AppButton>

					<AppTextField
						class="controls__search"
						placeholder="Search"
						icon-right="search"
						v-model="filters.keyword"
						@input="search"
					/>
				</div>
			</header>

			<AppTable
				class="notification-post__table"
				:fields="fields"
				:dataset="notifications.data"
				:selected="selected"
				@select="selected = [...$event]"
			>
				<template #status="{ props: { item } }">
					{{ item.is_sent ? 'Posted' : 'In Queue' }}
				</template>

				<template #created_at="{ props: { item } }">
					{{ item.created_at | dateFormatter('MMM DD, YYYY hh:mm A') }}
				</template>

				<template #country="{ props: { item } }">
					<span v-if="item.country.length === 0">
						All
					</span>

					<span v-else-if="item.country.length === 1">
						{{ item.country[0] }}
					</span>

					<a
						v-else
						class="clickable"
						@click="viewCountry(item.country)"
					>
						View
					</a>
				</template>

				<template #user="{ props: { item } }">
					<span v-if="item.users.length === 0">
						All
					</span>

					<a
						class="clickable"
						v-else
						@click="selectViewUser(item)"
					>
						View
					</a>
				</template>

				<template #notification_date="{ props: { item } }">
					{{ item.notification_date | dateFormatter('MMM DD, YYYY hh:mm A') }}
				</template>

				<template #actions="{ props: { item } }">
					<div class="controls">
						<AppButton
							class="controls__button"
							modifier="icon small-primary"
							:disabled="item.is_sent || buttonLoading"
							@click="editItem(item)"
						>
							<AppIcon
								class="controls__icon"
								icon-name="edit"
							/>
						</AppButton>

						<AppButton
							class="controls__button"
							modifier="icon small-danger"
							:disabled="buttonLoading"
							@click="deleteItem(false, item)"
						>
							<AppIcon
								class="controls__icon"
								icon-name="delete"
							/>
						</AppButton>
					</div>
				</template>

				<template #loading>
					<AppLoading/>
				</template>
			</AppTable>

			<div class="notification-post__pagination">
				<p>
					{{`${notifications.from || ''}-${notifications.to || ''}`}}
				</p>

				<AppCountPicker
					placeholder="Item"
					min="10"
					max="60"
					step="10"
					v-model="filters.itemsPerPage"
					:disabled="notificationLoading"
					@change="getList"
				/>

				<AppPagination
					v-model="filters.page"
					:total="notifications.last_page"
					:disabled="notificationLoading"
					@change="getList(false)"
				/>
			</div>

			<SidePanel
				v-if="showCountryPanel"
				@close="toggleCountryPanel(false)"
			>
				<PanelList
					title="Country List"
					description="List of notified Country"
					:items="countries"
					:fields="['name']"
				>
					<template #name="{ item }">
						{{ item }}
					</template>
				</PanelList>

			</SidePanel>

			<SidePanel
				v-if="showUserPanel"
				@close="toggleUserPanel(false)"
			>
				<PanelList
					title="User List"
					description="List of sent notification"
					:items="userViewList.data"
					:fields="['avatar', 'id', 'name']"
					:is-loading="userViewLoading"
					:from="userViewList.from"
					:to="userViewList.to"
					:total="userViewList.total"
					@prev="changeViewUserPage(-1)"
					@next="changeViewUserPage(1)"
				>
					<template
						#avatar="{ item }"
					>
						<AppImage
							:src="item.image ? `${URL}/${item.image}` : require('@/assets/img/default-avatar.png')"
							:fallback="require('@/assets/img/default-avatar.png')"
							title="avatar"
							class="avatar"
						/>
					</template>
				</PanelList>
			</SidePanel>

			<AppPopup
				:visible="modalName.includes('delete-notification')"
				type="warning"
				title="Warning"
				message="This notification will be deleted."
				@close="$store.dispatch('closeModal')"
			>
				<AppButton @click="$store.dispatch('confirm')" modifier="small-primary">Ok</AppButton>
				<AppButton @click="$store.dispatch('closeModal')" modifier="small-danger">Cancel</AppButton>
			</AppPopup>
		</LayoutBox>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import * as notificationModule from '@/store/notification'
import NotificationFilter from '@/class/NotificationFilter.js'
import PanelList from '@/components/layouts/PanelList.vue'
import SidePanel from '@/components/layouts/SidePanel.vue'

export default {
	beforeCreate () {
		if (!this.$store._modulesNamespaceMap['notification/']) {
			this.$store.registerModule('notification', notificationModule.default)
		}
	},

	async created () {
		await this.checkParams()
		this.getList()
	},

	data () {
		return {
			URL: process.env.VUE_APP_BASE,

			selected: [],

			showCountryPanel: false,
			showUserPanel: false,

			countries: [],
			users: [],

			filters: {
				keyword: '',
				page: 1,
				totalCount: 200,
				itemsPerPage: 30,
				sort: 'Latest',
				status: 'All'
			},

			fields: [
				{ name: 'id', text: 'ID' },
				{ name: 'status', text: 'Status' },
				{ name: 'created_at', text: 'Date Created' },
				{ name: 'title', text: 'Post Titile' },
				{ name: 'description', text: 'Notification Description' },
				{ name: 'country', text: 'Country' },
				{ name: 'user', text: 'User' },
				{ name: 'notification_date', text: 'Notification Date' },
				{ name: 'actions', text: 'Action' }
			],

			viewUser: {
				page: 1,
				id: 0
			}
		}
	},

	computed: {
		...mapFields('notification', {
			notifications: 'notifications',
			notificationLoading: 'notificationLoading',
			userViewList: 'userViewList',
			userViewLoading: 'userViewLoading',
			buttonLoading: 'buttonLoading'
		}),

		...mapGetters({
			modalName: 'modalName'
		})
	},

	methods: {

		getList (resetPage = true) {
			if (resetPage) this.filters.page = 1
			this.selected = []
			const { apiQuery, routerQuery } = new NotificationFilter(this.filters)
			this.$store.dispatch('notification/getList', apiQuery)
			this.$router.push({ query: routerQuery })
		},

		async checkParams () {
			const {
				page,
				order_by,
				status,
				count,
				keyword
			} = this.$route.query

			if (page) {
				this.filters.page = parseInt(page)
			}

			if (keyword) {
				this.filters.keyword = keyword
			}

			if (order_by) {
				this.filters.sort = order_by.charAt(0).toUpperCase() + order_by.slice(1)
			}

			if (status) {
				this.filters.status = status === 'queue' ? 'In Queue' : status
			}

			if (count) {
				this.filters.itemsPerPage = parseInt(count)
			}
		},

		async search (e) {
			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.filters.page = 1
				this.getList()
			}, 500)
		},

		changeViewUserPage (value) {
			this.viewUser.page += value
			this.viewUserList()
		},

		selectViewUser ({ id }) {
			this.viewUser.id = id
			this.viewUser.page = 1
			this.viewUserList()
			this.toggleUserPanel(true)
		},

		viewUserList () {
			const { id, page } = this.viewUser
			const apiQuery = `?id=${id}&page=${page}`
			this.$store.dispatch('notification/viewUser', apiQuery)
		},

		viewCountry (countries) {
			this.countries = countries
			this.toggleCountryPanel(true)
		},

		editItem ({ id, notification_date }) {
			const { filters } = this.$options
			const isPostDate = filters.isPostDate(new Date(notification_date))

			if (!isPostDate) {
				this.$router.push(`notification-post/edit/${id}`)
				return
			}

			this.$store.dispatch('toggleBanner', {
				status: true,
				message: 'Notification cannot be edited.',
				type: 'invalid'
			})
		},

		deleteItem (isMultiple = false, item = {}) {
			const vue = this
			const ids = this.selected.map(i => i.id)
			const callback = async function (page) {
				await vue.checkParams()
				vue.filters.page = page
		        await vue.getList(false)
			}

			let form = new FormData()
			form.append('id', isMultiple ? ids : item.id)
			form.append('page', this.filters.page)
			form.append('order_by', this.filters.sort)
			if (this.filters.status !== 'all') form.append('filter_by', this.filters.status)
			if (this.filters.status !== 30) form.append('count', this.filters.itemsPerPage)
			if (this.filters.keyword !== '') form.append('keyword', this.filters.keyword)

			this.$store.commit('toggleModal', {
				modalName: 'delete-notification',
				confirm: async () => {
					let isSuccess = await this.$store.dispatch('notification/delete', { form, callback, selected: this.selected })
					if (isSuccess) this.selected = []
				}
			})
		},

		toggleCountryPanel (status) {
			this.showCountryPanel = status
		},

		toggleUserPanel (status) {
			// this.viewUser.page = 1
			// this.viewUser.id = 0
			this.showUserPanel = status
		}
	},

	components: {
		SidePanel,
		PanelList
	}
}
</script>

<style lang="scss" scoped>
.notification-post-container {
	height: 100%;
	width: 100%;
	padding: 1.5em;
}

.notification-post {
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;

	&__header {
		display: flex;
		justify-content: space-between;
		margin-bottom: 1em;
	}

	&__table {
		flex: 1 1 auto;
		margin-bottom: 10px;
	}

	.clickable {
		color: $primary;
		cursor: pointer;
	}

	.controls {
		display: flex;
		align-items: flex-end;

		> *:not(:last-child) {
			margin-right: 1em;
		}

		&__dropdown { width: 100px; }
		&__search { width: 200px; }
		&__icon { width: 1em; }
		&__button:disabled { filter: grayscale(100%); }
	}

	&__pagination {
		width: 100%;
		display: flex;
		align-items: center;

		> *:first-child { margin-right: auto; }
		> *:not(:first-child) { margin-left: 20px; }
	}

	.avatar {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background-size: cover;
	}
}
</style>
